import React, { useState } from 'react';
import { PAID_PLANS, PLAN_DEAL_LIMITS, PlanId, PlanInterval, PlanPrice } from '../utils/shared/constants';
import { useText } from '../context/TextContext';
import { useOrganization } from '../context/OrganizationContext';
import RadioButtonsGroup from './RadioButtonsGroup';
import { formatPrice } from '../utils/shared/priceCalculator';
import { toast } from 'react-toastify';
import { useAuth } from 'reactfire';
import { initiatePlanUpdate } from '../utils/api';
import { AdvancedButton } from './AdvancedButton';
import UsageSection from './UsageSection';
import { useDeals } from '../hooks/useDeals';
import { getActiveDealsCount } from '../utils/shared/deal';
import { Badge } from 'react-bootstrap';
import { formatDate } from '../utils/date';

interface PlanCardInfo {
    id: PlanId;
    name: string;
    dealLimit: number;
    prices: Record<PlanInterval, PlanPrice>;
}

export const BillingTab = () => {
    const { currentPlanId, currentPlanDealLimit, orgId, organization } = useOrganization();
    const text = useText();
    const [annually, setAnnually] = useState(true);
    const [processing, setProcessing] = useState(false);
    const auth = useAuth();
    const { allDeals } = useDeals();

    const trialEndsAt = organization.trialEndsAt ? new Date(organization.trialEndsAt) : null;
    const isOnTrial = !!trialEndsAt && trialEndsAt > new Date();


    const allPlans: PlanCardInfo[] = [
        {
            id: 'free',
            name: text.free,
            dealLimit: PLAN_DEAL_LIMITS['free'],
            prices: {
                monthly: { cost: '0', currency: 'sek' },
                yearly: { cost: '0', currency: 'sek' },
            }
        },
        ...PAID_PLANS.map(plan => ({
            id: plan.id,
            name: plan.name,
            dealLimit: PLAN_DEAL_LIMITS[plan.id],
            prices: plan.prices,
        })),
    ];
    const planTagLines = [
        text.planTagLine0,
        text.planTagLine1,
        text.planTagLine2,
        text.planTagLine3,
    ];
    const planTagLinesUnderDeals = [
        text.planTagLineUnder0,
        text.planTagLineUnder1,
        text.planTagLineUnder2,
        text.planTagLineUnder3,
    ];
    const planBenefits = [
        [text.planBenefit00, text.planBenefit01],
        [text.planBenefit10, text.planBenefit11],
        [text.planBenefit20, text.planBenefit21],
        [text.planBenefit30, text.planBenefit31],
    ];

    const handlePlanSelect = async (planId: PlanId) => {
        setProcessing(true);

        try {
            const token = await auth.currentUser?.getIdToken() || '';
            const { url } = await initiatePlanUpdate(token, orgId, planId, annually);
            if (!url) {
                throw new Error('No url in response')
            }
            window.location.href = url
        } catch (error) {
            console.error('Failed to update plan', error);
            toast.error(text.defaultError)
        }

        setProcessing(false);
    }

    return (
        <div className="mt-3 d-flex flex-column gap-4">
            {isOnTrial && (
                <div className="alert alert-info mb-0">
                    <i className="bi bi-hourglass-split me-2" />
                    <strong>{text.trialActive}</strong> — {text.trialActiveUntil} {formatDate(trialEndsAt)}
                </div>
            )}
            <div className="d-flex flex-column gap-3">
                <strong>{text.billingHeading}</strong>
                <span>{text.billingIntro1}</span>
                <span>{text.billingIntro2}</span>
                <ul className="checklist">
                    <li>{text.billingFeature1}</li>
                    <li>{text.billingFeature2}</li>
                    <li>{text.billingFeature3}</li>
                </ul>
            </div>
            <div className="d-flex flex-column gap-3">
                <strong>{text.billingWhyTitle}</strong>
                <span>{text.billingWhyPara1}</span>
                <span>{text.billingWhyPara2}</span>
                <ul className="checklist orange">
                    <li>{text.billingWhyBullet1}</li>
                    <li>{text.billingWhyBullet2}</li>
                    <li>{text.billingWhyBullet3}</li>
                    <li>{text.billingWhyBullet4}</li>
                </ul>
            </div>
            <div className='center'>

                <strong>{text.availablePlans}</strong>
            </div>
            <div className='center'>
                <RadioButtonsGroup
                    name='interval'
                    className="center"
                    optionClassName="m-3"
                    options={[
                        { label: text.billedAnnually, onSelected: () => setAnnually(true), checked: annually },
                        { label: text.billedMonthly, onSelected: () => setAnnually(false), checked: !annually }
                    ]} />
            </div>
            <div className="d-flex justify-content-center flex-wrap gap-3">
                {allPlans.map((plan, index) => {

                    let displayName = plan.name;
                    if (displayName.startsWith('ArbTask')) {
                        displayName = displayName.replace('ArbTask ', '').trim();
                    }

                    return <div key={plan.id} className={`card shadow-sm${currentPlanId === plan.id ? ' border-success' : ''}`}
                        style={{ minWidth: 220, maxWidth: 260, flex: '1 1 220px' }}>
                        <div className="card-body d-flex flex-column align-items-start gap-2">
                            <Badge pill className="bright-highlight">{displayName}</Badge>
                            <div className="text-muted x-small" >{planTagLines[index]}</div>
                            <div className='mb-5' />

                            <div className='d-flex gap-1 align-items-end border-bottom w-100'>
                                <span className="display-6 fw-bold" style={{ lineHeight: 1 }}>{plan.dealLimit === -1 ? text.unlimited : plan.dealLimit}</span>
                                <span>{text.activeDeals}</span>
                            </div>
                            <div className="text-muted x-small">{planTagLinesUnderDeals[index]}</div>

                            <ul className='small ps-3'>
                                {planBenefits[index].map((benefit, i) => (
                                    <li key={i}>{benefit}</li>
                                ))}
                            </ul>


                            <div className='flex-1 d-flex flex-column justify-content-end align-items-start mt-3 w-100 gap-2'>
                                {
                                    currentPlanId === plan.id && (
                                        <AdvancedButton variant='success' disabled>{text.current}</AdvancedButton>
                                    )
                                }
                                {
                                    plan.id !== 'free' && (
                                        <>
                                            <div >
                                                {annually ? (<>

                                                    <div className="fw-bold">
                                                        {formatPrice(Number(plan.prices.yearly.cost) / 100 / 12)} {plan.prices.yearly.currency}<span className="text-muted">{text.perMonth}</span>
                                                    </div>
                                                    <small className='x-small'>{formatPrice(Number(plan.prices.yearly.cost) / 100)} {plan.prices.yearly.currency}<span className="text-muted">{text.perYear}</span></small>
                                                </>
                                                ) : (
                                                    <>
                                                        <div className="fw-bold">
                                                            {formatPrice(Number(plan.prices.monthly.cost) / 100)} {plan.prices.monthly.currency}<span className="text-muted">{text.perMonth}</span>
                                                        </div>

                                                    </>
                                                )}
                                            </div>

                                            {currentPlanId === 'free' && <div className='center'>
                                                <AdvancedButton onClick={() => handlePlanSelect(plan.id)} processing={processing}>{text.selectPlan}</AdvancedButton>
                                            </div>}
                                        </>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                })}
            </div >

            <UsageSection activeDealsCount={getActiveDealsCount(allDeals)} planDealLimit={currentPlanDealLimit} />

            <div className="text-muted small" style={{ opacity: 0.8 }}>
                {text.billingFooter1}<br />
                {text.billingFooter2}
            </div>
        </div >
    );
};
